import React from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {
    Delete as DeleteIcon,
    OpenInNew as OpenInNewIcon,
    RestaurantMenu as RestaurantMenuIcon,
    UploadFile as UploadFileIcon,
} from "@mui/icons-material";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const MENU_TYPES = [
    { type: "speisekarte", label: "Speisekarte" },
    { type: "getraenkekarte", label: "Getränkekarte" },
    { type: "dessertkarte", label: "Dessertkarte" },
    { type: "special", label: "Special" },
    { type: "tageskarte", label: "Tageskarte" },
    { type: "take_away", label: "Take-away" },
];

function resolveUrl(pathOrUrl) {
    if (!pathOrUrl) return "";
    const value = String(pathOrUrl).trim();
    if (/^https?:\/\//i.test(value)) return value;
    return `${API_BASE}${value.startsWith("/") ? value : `/${value}`}`;
}

function menuMap(company) {
    const out = {};
    (Array.isArray(company?.menus) ? company.menus : []).forEach((menu) => {
        out[menu.type] = menu;
    });
    return out;
}

export default function SectionSpeisekarten({
    company,
    submitting,
    menusLoading,
    menusError,
    onUploadMenu,
    onDeleteMenu,
    onSaveMenuUrl,
}) {
    const [dragType, setDragType] = React.useState(null);
    const [urlDrafts, setUrlDrafts] = React.useState({});
    const byType = React.useMemo(() => menuMap(company), [company]);

    React.useEffect(() => {
        const next = {};
        MENU_TYPES.forEach((item) => {
            next[item.type] = byType[item.type]?.external_url || "";
        });
        setUrlDrafts(next);
    }, [byType]);

    const pickFile = (type, file) => {
        if (!file) return;
        onUploadMenu?.(type, file);
    };

    const saveUrl = (type) => {
        onSaveMenuUrl?.(type, urlDrafts[type] || "");
    };

    if (!company?.id) {
        return (
            <Alert severity="info">
                Speisekarten können nach dem ersten Speichern der Firma hochgeladen werden.
            </Alert>
        );
    }

    return (
        <Box>
            {menusError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {menusError}
                </Alert>
            )}

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                    gap: 1.5,
                }}
            >
                {MENU_TYPES.map((item) => {
                    const menu = byType[item.type];
                    const pages = Array.isArray(menu?.pages) ? menu.pages : [];
                    const firstPage = pages[0];
                    const firstUrl = resolveUrl(firstPage?.url);
                    const externalUrl = menu?.external_url || "";
                    const openUrl = externalUrl || firstUrl;
                    const busy = submitting || menusLoading;
                    const activeDrop = dragType === item.type;

                    return (
                        <Paper
                            key={item.type}
                            variant="outlined"
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDragType(item.type);
                            }}
                            onDragLeave={() => setDragType(null)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragType(null);
                                pickFile(item.type, e.dataTransfer.files?.[0]);
                            }}
                            sx={{
                                p: 1.5,
                                borderColor: activeDrop ? "primary.main" : "divider",
                                bgcolor: activeDrop ? "action.hover" : "background.paper",
                                display: "grid",
                                gridTemplateColumns: firstUrl ? "92px minmax(0, 1fr)" : "1fr",
                                gap: 1.5,
                                alignItems: "stretch",
                            }}
                        >
                            {firstUrl && (
                                <Box
                                    component="a"
                                    href={firstUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        width: 92,
                                        height: 124,
                                        overflow: "hidden",
                                        borderRadius: 1,
                                        bgcolor: "grey.100",
                                        border: "1px solid",
                                        borderColor: "divider",
                                        display: "block",
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={firstUrl}
                                        alt={item.label}
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            display: "block",
                                        }}
                                    />
                                </Box>
                            )}

                            <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                    <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: 1 }}>
                                        <RestaurantMenuIcon fontSize="small" color={menu || externalUrl ? "primary" : "disabled"} />
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }} noWrap>
                                            {item.label}
                                        </Typography>
                                    </Box>
                                    {(menu || externalUrl) && (
                                        <Chip
                                            size="small"
                                            label={externalUrl ? "Link" : pages.length > 1 ? `${pages.length} Seiten` : "1 Seite"}
                                            variant="outlined"
                                        />
                                    )}
                                </Box>

                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {externalUrl || menu?.original_name || "Keine Datei oder Link hinterlegt"}
                                </Typography>

                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="stretch">
                                    <TextField
                                        size="small"
                                        label="Alternativer Link"
                                        value={urlDrafts[item.type] || ""}
                                        onChange={(e) => setUrlDrafts((drafts) => ({ ...drafts, [item.type]: e.target.value }))}
                                        disabled={busy}
                                        fullWidth
                                    />
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={busy}
                                        onClick={() => saveUrl(item.type)}
                                        sx={{ flexShrink: 0 }}
                                    >
                                        Speichern
                                    </Button>
                                </Stack>

                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: "auto" }}>
                                    <Button
                                        size="small"
                                        variant={menu ? "outlined" : "contained"}
                                        component="label"
                                        startIcon={busy ? <CircularProgress size={16} /> : <UploadFileIcon />}
                                        disabled={busy}
                                    >
                                        {menu ? "Ersetzen" : "Hochladen"}
                                        <input
                                            hidden
                                            type="file"
                                            accept="application/pdf,image/jpeg,.pdf,.jpg,.jpeg"
                                            onChange={(e) => {
                                                pickFile(item.type, e.target.files?.[0]);
                                                e.target.value = "";
                                            }}
                                        />
                                    </Button>

                                    {openUrl && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            href={openUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            startIcon={<OpenInNewIcon />}
                                        >
                                            Öffnen
                                        </Button>
                                    )}

                                    {(menu || externalUrl) && (
                                        <IconButton
                                            size="small"
                                            color="error"
                                            disabled={busy}
                                            onClick={() => onDeleteMenu?.(item.type)}
                                            aria-label={`${item.label} löschen`}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Stack>
                            </Box>
                        </Paper>
                    );
                })}
            </Box>
        </Box>
    );
}
